import { useState, useEffect } from "react";
import { adminGetAgentApplications, adminApproveAgent, adminRejectAgent, adminGetAgentApplicationDetail } from "@/api";
import { toast } from "sonner";
import { UserPlus, Search, CheckCircle, XCircle, Loader2, Eye, Clock, X, MapPin, Phone, Mail, CreditCard, Building, User, Shield, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export default function AdminAgentApplications() {
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { loadApplications(); }, [filter]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const res = await adminGetAgentApplications(params);
      setApplications(res.data.applications || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      toast.error("Failed to load applications");
    } finally { setLoading(false); }
  };

  const openDetail = async (app) => {
    setSelectedApp(app);
  };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      const res = await adminApproveAgent(id);
      toast.success(res.data.message || "Agent approved!");
      setSelectedApp(null);
      loadApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    } finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error("Please enter a rejection reason"); return; }
    setActionLoading(true);
    try {
      await adminRejectAgent(rejectModal, rejectReason);
      toast.success("Application rejected");
      setRejectModal(null);
      setRejectReason('');
      setSelectedApp(null);
      loadApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally { setActionLoading(false); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const filteredApps = applications.filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (a.name || '').toLowerCase().includes(s) || (a.phone || '').includes(s) || (a.email || '').toLowerCase().includes(s) || (a.city || '').toLowerCase().includes(s);
  });

  return (
    <div className="fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Agent Applications</h1>
          <p className="text-sm text-gray-500 mt-1">Review and approve agent registrations</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, mobile, email, city..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)} className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${filter === f.value ? 'bg-[#1565C0] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {f.label} {f.value === 'pending' && counts.pending > 0 ? <span className="ml-1 bg-white/20 text-white rounded-full px-1.5 text-[10px]">{counts.pending}</span> : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#1565C0]" /></div>
      ) : filteredApps.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <UserPlus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No {filter || ''} applications found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Mobile</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">City</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">PAN</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Applied</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredApps.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {app.profile_photo_url ? (
                          <img src={app.profile_photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#F0F4FF] flex items-center justify-center">
                            <User className="h-4 w-4 text-[#1565C0]" />
                          </div>
                        )}
                        <span className="font-semibold text-[#0A2540]">{app.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{app.phone}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{app.email}</td>
                    <td className="px-4 py-3 text-gray-600">{app.city || '-'}, {app.state || ''}</td>
                    <td className="px-4 py-3 font-mono text-xs">{app.pan_number || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{app.applied_at ? new Date(app.applied_at).toLocaleDateString() : app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(app.application_status || 'pending')}`}>
                        {(app.application_status || 'pending').charAt(0).toUpperCase() + (app.application_status || 'pending').slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button onClick={() => openDetail(app)} variant="ghost" size="sm" className="text-[#1565C0]">
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="font-bold text-lg text-[#0A2540]">Agent Application</h2>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusBadge(selectedApp.application_status || 'pending')}`}>
                  {(selectedApp.application_status || 'pending').toUpperCase()}
                </span>
              </div>
              <button onClick={() => setSelectedApp(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Profile */}
              <div className="flex items-center gap-4">
                {selectedApp.profile_photo_url ? (
                  <img src={selectedApp.profile_photo_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-[#F0F4FF] flex items-center justify-center">
                    <User className="h-8 w-8 text-[#1565C0]" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-xl text-[#0A2540]">{selectedApp.name}</h3>
                  <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {selectedApp.phone}</span>
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {selectedApp.email}</span>
                  </div>
                </div>
              </div>

              {/* Personal Info */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2"><MapPin className="h-4 w-4" /> Location</h4>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
                  <div><span className="text-xs text-gray-500">City</span><p className="font-semibold text-sm">{selectedApp.city || 'N/A'}</p></div>
                  <div><span className="text-xs text-gray-500">State</span><p className="font-semibold text-sm">{selectedApp.state || 'N/A'}</p></div>
                </div>
              </div>

              {/* KYC */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2"><Shield className="h-4 w-4" /> KYC Details</h4>
                <div className="grid grid-cols-2 gap-4 bg-blue-50 rounded-xl p-4">
                  <div><span className="text-xs text-gray-500">PAN Number</span><p className="font-semibold text-sm font-mono">{selectedApp.pan_number || 'N/A'}</p></div>
                  <div><span className="text-xs text-gray-500">Aadhaar Number</span><p className="font-semibold text-sm font-mono">{selectedApp.aadhaar_number ? '****' + selectedApp.aadhaar_number.slice(-4) : 'N/A'}</p></div>
                </div>
              </div>

              {/* Bank Details */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2"><Building className="h-4 w-4" /> Bank Details</h4>
                <div className="grid grid-cols-2 gap-4 bg-green-50 rounded-xl p-4">
                  <div><span className="text-xs text-gray-500">Account Number</span><p className="font-semibold text-sm font-mono">{selectedApp.bank_account ? '****' + selectedApp.bank_account.slice(-4) : 'N/A'}</p></div>
                  <div><span className="text-xs text-gray-500">IFSC Code</span><p className="font-semibold text-sm font-mono">{selectedApp.ifsc || 'N/A'}</p></div>
                </div>
              </div>

              {/* Referral Code (if approved) */}
              {selectedApp.referral_code && (
                <div className="bg-[#F0F4FF] rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#1565C0]">Referral Code</span>
                    <p className="font-mono font-bold text-xl text-[#0A2540] mt-1">{selectedApp.referral_code}</p>
                  </div>
                  <span className="text-xs text-gray-500">Referrals: {selectedApp.total_referrals || 0}</span>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedApp.rejection_reason && (
                <div className="bg-red-50 rounded-xl p-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Rejection Reason</span>
                  <p className="text-sm mt-1 text-red-800">{selectedApp.rejection_reason}</p>
                </div>
              )}

              {/* Applied date */}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="h-3 w-3" />
                Applied: {selectedApp.applied_at ? new Date(selectedApp.applied_at).toLocaleString() : selectedApp.created_at ? new Date(selectedApp.created_at).toLocaleString() : 'N/A'}
                {selectedApp.approved_at && <> | Approved: {new Date(selectedApp.approved_at).toLocaleString()}</>}
              </div>

              {/* Action Buttons */}
              {(selectedApp.application_status === 'pending' || !selectedApp.application_status) && (
                <div className="border-t pt-6 flex gap-3">
                  <Button onClick={() => handleApprove(selectedApp.id)} disabled={actionLoading} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-semibold">
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-2" /> Approve Agent</>}
                  </Button>
                  <Button onClick={() => { setRejectModal(selectedApp.id); }} disabled={actionLoading} variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-xl py-3 font-semibold">
                    <XCircle className="h-4 w-4 mr-2" /> Reject Application
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-bold text-lg text-[#0A2540] mb-4">Reject Application</h3>
            <p className="text-sm text-gray-500 mb-4">Please provide a reason for rejection. This will be shown to the agent.</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Enter rejection reason..." rows={3} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 mb-4" />
            <div className="flex gap-3">
              <Button onClick={() => { setRejectModal(null); setRejectReason(''); }} variant="outline" className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={handleReject} disabled={actionLoading || !rejectReason.trim()} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
